import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { getEmployees, createEmployee, updateEmployee, deleteEmployee } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Plus, Pencil, Trash, UserGear, ShieldCheck, Truck, Package, Wrench } from '@phosphor-icons/react';
import { toast } from 'sonner';

const ROLES = [
  { id: 'admin', label: 'Administrator', icon: ShieldCheck, color: 'bg-red-100 text-red-800' },
  { id: 'sales_agent', label: 'Sales Agent', icon: Package, color: 'bg-blue-100 text-blue-800' },
  { id: 'logistics_manager', label: 'Logistics Manager', icon: Truck, color: 'bg-purple-100 text-purple-800' },
  { id: 'operations', label: 'Operations', icon: Wrench, color: 'bg-amber-100 text-amber-800' }
];

const PERMISSIONS = [
  { id: 'view_orders', label: 'View Orders', group: 'Orders' },
  { id: 'add_order', label: 'Add Order', group: 'Orders' },
  { id: 'edit_orders', label: 'Edit Orders', group: 'Orders' },
  { id: 'delete_orders', label: 'Delete Orders', group: 'Orders' },
  { id: 'see_own_orders_only', label: 'See Own Orders Only', group: 'Orders' },
  { id: 'view_clients', label: 'View Clients', group: 'Clients' },
  { id: 'edit_clients', label: 'Edit Clients', group: 'Clients' },
  { id: 'view_finances', label: 'View Finances', group: 'Finance' },
  { id: 'change_finance', label: 'Change Finance', group: 'Finance' },
  { id: 'can_see_purchase_price', label: 'See Purchase Price (sales agents hidden by default)', group: 'Finance' },
  { id: 'see_sell_price', label: 'See Sell Price', group: 'Finance' },
];

const PERMISSION_GROUPS = ['Orders', 'Clients', 'Finance'];

const EmployeesPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [formData, setFormData] = useState({
    email: '', password: '', name: '', role: '', phone: '', telegram_chat_id: '', is_active: true, permissions: {}
  });

  const loadEmployees = useCallback(async () => {
    try {
      const res = await getEmployees();
      setEmployees(res.data);
    } catch {
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadEmployees(); }, [loadEmployees]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedEmployee) {
        // Build update payload — include email/password ONLY if changed
        const payload = {
          name: formData.name,
          role: formData.role,
          phone: formData.phone || '',
          telegram_chat_id: formData.telegram_chat_id || '',
          is_active: formData.is_active,
          permissions: formData.permissions || {},
        };
        if (formData.email && formData.email.toLowerCase() !== selectedEmployee.email?.toLowerCase()) {
          payload.email = formData.email;
        }
        if (formData.password && formData.password.trim().length >= 6) {
          payload.password = formData.password;
        }
        await updateEmployee(selectedEmployee.id, payload);
        toast.success('Employee updated');
      } else {
        await createEmployee(formData);
        toast.success('Employee created');
      }
      setDialogOpen(false);
      resetForm();
      loadEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (employee) => {
    if (employee.id === user.id) {
      toast.error('Cannot delete yourself');
      return;
    }
    if (!window.confirm(`Delete employee "${employee.name}"?`)) return;
    try {
      await deleteEmployee(employee.id);
      toast.success('Employee deleted');
      loadEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const openEdit = (employee) => {
    setSelectedEmployee(employee);
    setFormData({
      email: employee.email || '',
      password: '',
      name: employee.name || '',
      role: employee.role || '',
      phone: employee.phone || '',
      telegram_chat_id: employee.telegram_chat_id || '',
      is_active: employee.is_active ?? true,
      permissions: employee.permissions || {}
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setSelectedEmployee(null);
    setFormData({
      email: '', password: '', name: '', role: '', phone: '', telegram_chat_id: '', is_active: true, permissions: {}
    });
  };

  const togglePermission = (permId) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permId]: !prev.permissions[permId]
      }
    }));
  };

  const getRoleInfo = (roleId) => ROLES.find(r => r.id === roleId) || { label: roleId, color: 'bg-gray-100 text-gray-800' };

  const getActivePermissions = (perms) => {
    if (!perms) return [];
    return Object.entries(perms).filter(([, v]) => v).map(([k]) => k);
  };

  return (
    <div data-testid="employees-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('employees.title')}</h1>
          <p className="text-muted-foreground text-sm">Manage staff accounts and permissions</p>
        </div>
        <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="btn-primary" data-testid="add-employee-btn">
          <Plus className="w-4 h-4 mr-2" />
          {t('employees.addEmployee')}
        </Button>
      </div>

      {/* Employees Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : employees.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No employees found
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {employees.map((employee, idx) => {
            const roleInfo = getRoleInfo(employee.role);
            const RoleIcon = roleInfo.icon || UserGear;
            const activePerms = getActivePermissions(employee.permissions);
            return (
              <div key={employee.id} className={`card-brutalist animate-fade-in stagger-${(idx % 5) + 1}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-secondary flex items-center justify-center">
                      <RoleIcon weight="duotone" className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-bold">{employee.name}</h3>
                      <Badge className={roleInfo.color}>{roleInfo.label}</Badge>
                    </div>
                  </div>
                  {employee.id !== user.id && (
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(employee)} data-testid={`edit-employee-${employee.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(employee)} className="text-destructive" data-testid={`delete-employee-${employee.id}`}>
                        <Trash className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
                
                <div className="space-y-1 text-sm">
                  <p className="text-muted-foreground">{employee.email}</p>
                  {employee.phone && <p className="text-muted-foreground">{employee.phone}</p>}
                </div>

                {/* Permissions badges */}
                {activePerms.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {activePerms.map(p => {
                      const perm = PERMISSIONS.find(pm => pm.id === p);
                      return <span key={p} className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded">{perm?.label || p}</span>;
                    })}
                  </div>
                )}

                <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                  <span className={`text-xs px-2 py-0.5 ${employee.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                    {employee.is_active !== false ? 'Active' : 'Inactive'}
                  </span>
                  {employee.id === user.id && (
                    <span className="text-xs text-muted-foreground">(You)</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedEmployee ? t('employees.editEmployee') : t('employees.addEmployee')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="input-brutalist"
                data-testid="employee-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Email {!selectedEmployee && '*'}</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required={!selectedEmployee}
                className="input-brutalist"
                data-testid="employee-email-input"
              />
              {selectedEmployee && (
                <p className="text-[11px] text-muted-foreground">Changing the email will affect this employee's next login.</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Password {!selectedEmployee && '*'}</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!selectedEmployee}
                minLength={selectedEmployee ? undefined : 6}
                placeholder={selectedEmployee ? 'Leave empty to keep current password' : ''}
                className="input-brutalist"
                data-testid="employee-password-input"
              />
              {selectedEmployee && (
                <p className="text-[11px] text-muted-foreground">Minimum 6 characters. Leave empty to keep the existing password.</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>{t('employees.role')} *</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                <SelectTrigger className="input-brutalist" data-testid="employee-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((role) => (
                    <SelectItem key={role.id} value={role.id}>{role.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="input-brutalist"
              />
            </div>
            <div className="space-y-2">
              <Label>Telegram Chat ID <span className="text-muted-foreground text-xs font-normal">(optional)</span></Label>
              <Input
                value={formData.telegram_chat_id}
                onChange={(e) => setFormData({ ...formData, telegram_chat_id: e.target.value })}
                placeholder="e.g. 123456789"
                className="input-brutalist"
                data-testid="employee-telegram-chat-id-input"
              />
              <p className="text-[11px] text-muted-foreground">Employee will receive Telegram alerts for their own orders. To get your Chat ID, message <a href="https://t.me/userinfobot" target="_blank" rel="noopener noreferrer" className="text-primary underline">@userinfobot</a> on Telegram and it will reply with your numeric ID.</p>
            </div>

            {/* Permissions Matrix */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Permissions</Label>
              <div className="border border-border rounded-md p-3 space-y-3" data-testid="permissions-matrix">
                {PERMISSION_GROUPS.map(group => (
                  <div key={group}>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">{group}</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {PERMISSIONS.filter(p => p.group === group).map(perm => (
                        <label
                          key={perm.id}
                          className={`flex items-center gap-2 px-2.5 py-2 border text-xs cursor-pointer transition rounded ${
                            formData.permissions[perm.id]
                              ? 'bg-primary/5 border-primary/40 font-medium'
                              : 'border-border hover:bg-slate-50'
                          }`}
                          data-testid={`perm-${perm.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={!!formData.permissions[perm.id]}
                            onChange={() => togglePermission(perm.id)}
                            className="w-3.5 h-3.5 accent-primary"
                          />
                          {perm.label}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {selectedEmployee && (
              <div className="flex items-center justify-between">
                <Label>Active</Label>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
              </div>
            )}
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" className="btn-primary" data-testid="employee-submit-btn">
                {t('common.save')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeesPage;
